import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { getAccessTokenSync, useAccessToken } from '../../auth/useAccessToken';
import type { ApiError } from '../../api/client';
import {
  HAT_CONSOLE_POLL_MS,
  pollConsoleFrames,
  setCaptureFilter,
  setConsoleMode,
  startConsoleSession,
  stopConsoleSession,
} from '../../api/queries';
import type { ConsoleFrame } from '../../api/types';
import { buildAnalyserHandoffUrl, stashConsoleAuthToken, stashConsoleCapture } from '../../utils/consoleHandoff';
import { buildExportFilename } from '../../utils/export';
import { fmtDate, fmtDateTime, fmtOrEmDash } from '../../utils/format';

// Brand palette — matches the DialogProvider chrome so the console feels native.
const BRAND_BLUE = '#1f3b6f';

// Cap the in-memory scrollback so a chatty HAT (or a long session) can't grow
// the buffer without bound. Oldest lines drop off the top, terminal-style.
const MAX_SCROLLBACK = 2_000;

/**
 * Fire-and-forget the session teardown. Best-effort: the server-side dead-man
 * (45 s) is the backstop if it fails. Wrapped in Promise.resolve so a rejected
 * (or non-promise) return can never throw out of a React cleanup callback.
 */
function safeStop(sessionId: string, getToken: () => Promise<string | null>): void {
  void Promise.resolve(stopConsoleSession(sessionId, getToken)).catch(() => { /* best-effort */ });
}

// ---------------------------------------------------------------------------
// Session hook — start / poll / append / stop. Exported for unit testing.
// ---------------------------------------------------------------------------

/** The enriched 409 body when another user holds the console
 *  (console-session-contract.md) — parsed off `ApiError.details`. */
export type HeldBySession = {
  activeSessionId: string;
  heldByUserId: string;
  heldByDisplayName: string;
  startedAt: string;
};

export type ConsoleSessionState = {
  /** 'connecting' until the start POST resolves; then 'streaming'; 'held' when
   *  another user has the console open (start POST 409 console_session_active
   *  — see {@link heldBy}); 'ended' when the session closed; 'error' if the
   *  start POST (or a poll) failed hard for any other reason. */
  phase: 'connecting' | 'streaming' | 'held' | 'ended' | 'error';
  frames: ConsoleFrame[];
  /** Wall-clock captured once, the moment the session transitions to
   *  'streaming' (start POST resolved). Drives the one-time "Session opened
   *  at …" header — the per-frame lines carry their own HH:MM:SS.mmm from
   *  the firmware, so this is the only date the portal stamps itself. Null
   *  until streaming starts; reset to null on every fresh open. */
  sessionOpenedAt: Date | null;
  /** True from the first time {@link appendFrames} actually drops lines off the
   *  top because the scrollback exceeded {@link MAX_SCROLLBACK}. Drives the
   *  "oldest lines dropped" warning chip — download the capture to keep
   *  everything. Resets to false on a fresh session (open→true) or `clear()`;
   *  never affects `frames`/`phase` on its own. */
  truncated: boolean;
  /** The end reason once phase === 'ended' (stopped_by_user, dead-man, TTL, …). */
  endReason: string | null;
  /** The parsed ApiError once phase === 'error' — rendered verbatim in-UI. */
  error: ApiError | null;
  /** The other holder's details once phase === 'held' (parsed from the 409
   *  `console_session_active` body). Null otherwise. */
  heldBy: HeldBySession | null;
  /** True while a {@link takeover} call is in flight. */
  takingOver: boolean;
  /** The ApiError from the most recent failed {@link takeover} call, if any
   *  — rendered inline in the held-session panel; the panel stays up so the
   *  tech can retry rather than the dialog silently reverting to a generic
   *  error. Cleared on the next attempt. */
  takeoverError: ApiError | null;
  /** Re-issues the start POST with `{ takeover: true }`, ending whoever
   *  currently holds the console and opening a fresh session for this tab.
   *  Only meaningful while phase === 'held'. */
  takeover: () => void;
  paused: boolean;
  setPaused: (v: boolean) => void;
  clear: () => void;
  /** Explicit stop (the Stop button). Ends the session + stops the poll. */
  stop: () => void;
  /** Whether this HAT's firmware supports mode-B (raw SAS-comms capture),
   *  from the start-session response. False until the session has started. */
  modeBSupported: boolean;
  /** The console mode currently in effect ("A" = hat_diag, "B" = raw SAS
   *  capture). Only flips locally once the server confirms the switch. */
  mode: 'A' | 'B';
  /** The ApiError from the most recent failed {@link setMode} call, if any.
   *  Cleared on the next attempt. Never affects `frames`/`phase` — a failed
   *  mode switch never blanks or interrupts the live stream. */
  modeError: ApiError | null;
  /** Flip the session between mode A/B. Fire-and-forget from the caller's
   *  perspective (drives the Switch's `checked` via `mode`, not this call) —
   *  never touches `frames`, the poll timer, or `phase`. */
  setMode: (next: 'A' | 'B') => void;
  /** Whether a mode-B (raw SAS capture) session includes routine general-poll
   *  traffic in the stream. Defaults to `false` (filtered) for every fresh
   *  session — only flips locally once the server confirms the switch. */
  showGeneralPolls: boolean;
  /** The ApiError from the most recent failed {@link setShowGeneralPolls}
   *  call, if any. Cleared on the next attempt. Never affects `frames`/`phase`
   *  — a failed filter switch never blanks or interrupts the live stream. */
  captureFilterError: ApiError | null;
  /** Flip the mode-B general-poll capture filter. Fire-and-forget from the
   *  caller's perspective (drives the Switch's `checked` via
   *  `showGeneralPolls`, not this call) — never touches `frames`, the poll
   *  timer, or `phase`. */
  setShowGeneralPolls: (next: boolean) => void;
};

/**
 * Parses the enriched 409 body (console-session-contract.md) off an
 * ApiError's `details` when another user holds the console. Returns `null`
 * for any other error code, a missing `details` bag, or a body missing one
 * of the required fields — the caller falls back to the generic error
 * surface in that case rather than rendering a held-session panel with
 * holes in it.
 */
function parseHeldBySessionError(error: ApiError): HeldBySession | null {
  if (error.code !== 'console_session_active') return null;
  const d = error.details;
  if (!d) return null;
  const activeSessionId   = typeof d.activeSessionId   === 'string' ? d.activeSessionId   : null;
  const heldByUserId      = typeof d.heldByUserId      === 'string' ? d.heldByUserId      : null;
  const heldByDisplayName = typeof d.heldByDisplayName === 'string' ? d.heldByDisplayName : null;
  const startedAt         = typeof d.startedAt         === 'string' ? d.startedAt         : null;
  if (!activeSessionId || !heldByUserId || !heldByDisplayName || !startedAt) return null;
  return { activeSessionId, heldByUserId, heldByDisplayName, startedAt };
}

/**
 * Drives one live console session for `machineDeviceId` while `open` is true.
 *
 * Lifecycle:
 *  - open → POST start; on 200 begin polling frames every {@link HAT_CONSOLE_POLL_MS}.
 *  - each poll appends `frames` (in seq order) to the scrollback and advances the
 *    cursor to `nextSeq`.
 *  - `ended: true` (or an explicit Stop) stops the poll and records the reason.
 *  - on close/unmount → DELETE the session so nothing is left streaming
 *    (zero-leak invariant; the SC clears the HAT flag on its next poll).
 *
 * All I/O goes through the api client (queries.ts) so errors carry the server's
 * HTTP status + code + message for the UI. Timers are self-scheduling (a manual
 * setTimeout loop) rather than useQuery.refetchInterval so a single in-flight
 * request can never overlap the next, and pausing is a clean flag check.
 */
export function useConsoleSession(
  machineDeviceId: string | null,
  open: boolean,
): ConsoleSessionState {
  const getToken = useAccessToken();

  const [phase, setPhase] = useState<ConsoleSessionState['phase']>('connecting');
  const [frames, setFrames] = useState<ConsoleFrame[]>([]);
  const [sessionOpenedAt, setSessionOpenedAt] = useState<Date | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [endReason, setEndReason] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [heldBy, setHeldBy] = useState<HeldBySession | null>(null);
  const [takingOver, setTakingOver] = useState(false);
  const [takeoverError, setTakeoverError] = useState<ApiError | null>(null);
  const [paused, setPaused] = useState(false);
  const [modeBSupported, setModeBSupported] = useState(false);
  const [mode, setModeState] = useState<'A' | 'B'>('A');
  const [modeError, setModeError] = useState<ApiError | null>(null);
  const [showGeneralPolls, setShowGeneralPollsState] = useState(false);
  const [captureFilterError, setCaptureFilterError] = useState<ApiError | null>(null);

  // Refs the async loop reads without re-subscribing.
  const sessionIdRef = useRef<string | null>(null);
  const cursorRef = useRef<number>(0);
  const pausedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(false); // guards against a poll firing after teardown
  // Mirrors frames.length synchronously (setFrames is async) so appendFrames
  // can tell, without reading state inside the setFrames updater, whether
  // THIS append is the one that first exceeds the cap.
  const frameCountRef = useRef(0);

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const appendFrames = useCallback((incoming: ConsoleFrame[]) => {
    if (incoming.length === 0) return;
    const total = frameCountRef.current + incoming.length;
    if (total > MAX_SCROLLBACK) {
      frameCountRef.current = MAX_SCROLLBACK;
      setTruncated(true);
    } else {
      frameCountRef.current = total;
    }
    setFrames((prev) => {
      const next = prev.concat(incoming);
      // Terminal-style cap: keep the most recent MAX_SCROLLBACK lines.
      return next.length > MAX_SCROLLBACK ? next.slice(next.length - MAX_SCROLLBACK) : next;
    });
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // One poll iteration, then re-arm itself unless the session ended/errored.
  const pollOnce = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    if (!activeRef.current || !sessionId) return;
    if (pausedRef.current) {
      // Paused: don't hit the server, just re-check shortly. The session stays
      // open (the dead-man clock keeps ticking) — pause is a UI-only convenience.
      timerRef.current = setTimeout(pollOnce, HAT_CONSOLE_POLL_MS);
      return;
    }
    try {
      const page = await pollConsoleFrames(sessionId, cursorRef.current, getToken);
      if (!activeRef.current) return;
      appendFrames(page.frames);
      cursorRef.current = page.nextSeq;
      if (page.ended) {
        setEndReason(page.reason ?? 'ended');
        setPhase('ended');
        activeRef.current = false;
        clearTimer();
        return;
      }
      timerRef.current = setTimeout(pollOnce, HAT_CONSOLE_POLL_MS);
    } catch (e) {
      if (!activeRef.current) return;
      // A poll failure ends the live view but never hides the cause.
      setError(e as ApiError);
      setPhase('error');
      activeRef.current = false;
      clearTimer();
    }
  }, [appendFrames, clearTimer, getToken]);

  // Start + teardown, driven by `open`.
  useEffect(() => {
    if (!open || !machineDeviceId) return;

    let cancelled = false;
    // Reset per-open state.
    setPhase('connecting');
    setFrames([]);
    setSessionOpenedAt(null);
    setTruncated(false);
    setEndReason(null);
    setError(null);
    setHeldBy(null);
    setTakingOver(false);
    setTakeoverError(null);
    setPaused(false);
    pausedRef.current = false;
    cursorRef.current = 0;
    frameCountRef.current = 0;
    sessionIdRef.current = null;
    activeRef.current = false;
    setModeBSupported(false);
    setModeState('A');
    setModeError(null);
    setShowGeneralPollsState(false);
    setCaptureFilterError(null);

    (async () => {
      try {
        const res = await startConsoleSession(machineDeviceId, getToken);
        if (cancelled) {
          // Opened and closed before start resolved — tear the session down
          // immediately so we don't leak it.
          safeStop(res.sessionId, getToken);
          return;
        }
        sessionIdRef.current = res.sessionId;
        activeRef.current = true;
        setModeBSupported(res.modeBSupported);
        // `resumed: true` means the server handed back OUR OWN in-progress
        // session untouched — carry its current mode AND capture-filter
        // state forward rather than defaulting to "A"/false (the reset
        // above), and never re-send a mode or capture-filter call for it.
        // `res.mode`/`res.showGeneralPolls` are always present per the
        // contract; the `?? 'A'` / `?? false` only guard mocks/older
        // responses that omit them. `resumed: false` (fresh session, or a
        // takeover's fresh session) also carries both — server-default
        // "A"/false — so this is a no-op there.
        setModeState(res.mode ?? 'A');
        setShowGeneralPollsState(res.showGeneralPolls ?? false);
        setSessionOpenedAt(new Date());
        setPhase('streaming');
        void pollOnce();
      } catch (e) {
        if (cancelled) return;
        const err = e as ApiError;
        const held = parseHeldBySessionError(err);
        if (held) {
          // Another user holds the console — show the held-session panel
          // (with a Take-over button) instead of the generic error surface.
          setHeldBy(held);
          setPhase('held');
        } else {
          setError(err);
          setPhase('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      activeRef.current = false;
      clearTimer();
      const sessionId = sessionIdRef.current;
      sessionIdRef.current = null;
      if (sessionId) {
        // Zero-leak teardown: close the session on the RMS so the SC clears the
        // HAT flag and the HAT stops POSTing.
        safeStop(sessionId, getToken);
      }
    };
    // getToken/pollOnce are stable enough; re-run only when the target/open flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, machineDeviceId]);

  const clear = useCallback(() => {
    setFrames([]);
    frameCountRef.current = 0;
    setTruncated(false);
  }, []);

  const stop = useCallback(() => {
    const sessionId = sessionIdRef.current;
    activeRef.current = false;
    clearTimer();
    if (sessionId && phase !== 'ended') {
      safeStop(sessionId, getToken);
    }
    setEndReason((r) => r ?? 'stopped_by_user');
    setPhase('ended');
  }, [clearTimer, getToken, phase]);

  // Flip the session's console mode. Deliberately touches ONLY `mode` /
  // `modeBSupported`-independent state — never `frames`, `cursorRef`,
  // `timerRef`, or `phase` — so the frame poll (pollOnce, above) keeps
  // running through a toggle exactly as it was; the terminal never blanks or
  // remounts. On failure (e.g. the 409 sas_capture_unsupported_firmware rail)
  // the local `mode` is left as-is (the switch snaps back to reflect what the
  // server actually has) and the error is surfaced via `modeError`, never
  // swallowed.
  const setMode = useCallback((next: 'A' | 'B') => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    setModeError(null);
    void setConsoleMode(sessionId, next, getToken)
      .then(() => setModeState(next))
      .catch((e) => setModeError(e as ApiError));
  }, [getToken]);

  // Flip the mode-B general-poll capture filter. Same shape/discipline as
  // `setMode` above: deliberately touches ONLY `showGeneralPolls`-independent
  // state — never `frames`, `cursorRef`, `timerRef`, or `phase` — so the frame
  // poll keeps running through a toggle exactly as it was; the terminal never
  // blanks or remounts. On failure the local value is left as-is (the switch
  // snaps back to reflect what the server actually has) and the error is
  // surfaced via `captureFilterError`, never swallowed.
  const setShowGeneralPolls = useCallback((next: boolean) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    setCaptureFilterError(null);
    void setCaptureFilter(sessionId, next, getToken)
      .then(() => setShowGeneralPollsState(next))
      .catch((e) => setCaptureFilterError(e as ApiError));
  }, [getToken]);

  // Re-issue the start POST with `{ takeover: true }` from the held-session
  // panel. Per the contract this always ends up creating a fresh session
  // (whoever held it — even this same caller — is stopped first), so on
  // success this re-arms the poll loop exactly like the initial open did:
  // fresh cursor/frames, mode from the response, streaming. On failure the
  // panel stays up (phase remains 'held') with the error surfaced via
  // `takeoverError` so the tech can see what happened and retry, rather than
  // silently reverting to a dead dialog.
  const takeover = useCallback(() => {
    if (!machineDeviceId) return;
    setTakingOver(true);
    setTakeoverError(null);
    (async () => {
      try {
        const res = await startConsoleSession(machineDeviceId, getToken, { takeover: true });
        sessionIdRef.current = res.sessionId;
        activeRef.current = true;
        cursorRef.current = 0;
        frameCountRef.current = 0;
        setFrames([]);
        setTruncated(false);
        setEndReason(null);
        setModeBSupported(res.modeBSupported);
        setModeState(res.mode ?? 'A');
        setShowGeneralPollsState(res.showGeneralPolls ?? false);
        setHeldBy(null);
        setSessionOpenedAt(new Date());
        setPhase('streaming');
        setTakingOver(false);
        void pollOnce();
      } catch (e) {
        setTakeoverError(e as ApiError);
        setTakingOver(false);
      }
    })();
  }, [machineDeviceId, getToken, pollOnce]);

  return {
    phase, frames, sessionOpenedAt, truncated, endReason, error, heldBy, takingOver, takeoverError,
    takeover, paused, setPaused, clear, stop,
    modeBSupported, mode, modeError, setMode,
    showGeneralPolls, captureFilterError, setShowGeneralPolls,
  };
}

// ---------------------------------------------------------------------------
// Human-readable end reasons.
// ---------------------------------------------------------------------------

function endReasonLabel(reason: string | null): string {
  switch (reason) {
    case 'stopped_by_user': return 'Session stopped.';
    case 'dead_man':
    case 'dead-man':        return 'Session closed — the portal stopped polling (dead-man timeout).';
    case 'ttl':
    case 'ttl_expired':     return 'Session closed — reached the 15-minute time limit.';
    case 'hat_offline':     return 'HAT went offline — the diagnostics stream stopped.';
    default:                return reason ? `Session ended (${reason}).` : 'Session ended.';
  }
}

// ---------------------------------------------------------------------------
// Error surface — every failure shows HTTP code + server code/message.
// ---------------------------------------------------------------------------

function ConsoleErrorAlert({ error }: { error: ApiError }) {
  // The api client already prefixes the message with "METHOD path: HTTP NNN …";
  // we additionally spell out the well-known 409 codes so the tech knows what
  // to do next, and surface the firmware/holder detail the server returns.
  const detail = errorGuidance(error);
  const [copied, setCopied] = useState(false);
  // The footer "Copy" copies terminal frames and is disabled on an open-failure
  // (no frames); this dedicated button copies the actual error text so a tech can
  // paste it into a ticket.
  const copyError = useCallback(async () => {
    const text = detail ? `${detail}\n\n${error.message}` : error.message;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — silent, matches DialogProvider */ }
  }, [detail, error.message]);
  return (
    <Alert
      severity="error"
      variant="outlined"
      sx={{ m: 2 }}
      action={
        <Tooltip title={copied ? 'Copied' : 'Copy error'}>
          <IconButton size="small" color="inherit" onClick={copyError} aria-label="Copy error">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <AlertTitle>Couldn't open the console</AlertTitle>
      {detail && (
        <Typography variant="body2" sx={{ mb: 1 }}>{detail}</Typography>
      )}
      <Typography
        variant="caption"
        component="pre"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontFamily: 'monospace' }}
      >
        {error.message}
      </Typography>
    </Alert>
  );
}

function errorGuidance(error: ApiError): string | null {
  switch (error.code) {
    case 'not_commissioned':
      return 'This HAT is not commissioned to a site controller yet, so there is no path to its diagnostics stream.';
    case 'hat_offline':
      return 'The HAT is offline (its ESP32 link is down or it was last seen too long ago). Try again once it reconnects.';
    case 'console_unsupported_firmware':
      // WS2 returns currentFirmware / requiresFirmware alongside the message,
      // and the api client folds them into `message` — surface a plain hint too.
      return 'This HAT is running firmware that does not include the console task. Update the HAT firmware to a build that supports the remote console.';
    case 'console_session_active':
      // Normal path: parseHeldBySessionError + phase 'held' render the
      // dedicated held-session panel (with Take-over) instead of this
      // generic alert. This guidance only surfaces as a fallback — a 409
      // whose body was missing one of the required held-by fields.
      return 'Another console session is already open on this HAT. Only one console can be attached at a time — wait for the current holder to finish, or take over the session.';
    case 'not_found':
      return 'The HAT (or session) could not be found in your scope.';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// The live terminal tail.
// ---------------------------------------------------------------------------

/**
 * "Session opened at DD/MM/YYYY HH:MM" (local tz). Built entirely from the
 * existing date helpers — `fmtDate` for the calendar part, `toLocaleTimeString`
 * (the same 2-digit-hour/minute idiom used elsewhere in the portal, e.g.
 * SiteControllerHeartbeatPanel's heartbeat-age axis) for the clock part — no
 * hand-rolled date math.
 */
function formatSessionOpened(d: Date): string {
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `Session opened at ${fmtDate(d)} ${time}`;
}

/**
 * TX-red / RX-green colour for a raw SAS-capture (`kind === 'sas'`) line.
 * The firmware line is `HH:MM:SS.mmm <seq> <TX|RX> HH HH …`, so token index 2
 * (after time, seq) is the direction. Non-sas frames (diag / idle / dropped
 * markers) stay neutral — `undefined` lets the span inherit the terminal's
 * default text colour. The TX/RX text itself is always kept for colour-blind
 * readers; this only adds colour on top.
 */
function sasLineColor(f: ConsoleFrame): string | undefined {
  if (f.kind !== 'sas') return undefined;
  const dir = f.line.split(/\s+/)[2];
  if (dir === 'TX') return '#ff6b6b';
  if (dir === 'RX') return '#51cf66';
  return undefined;
}

// Matches the leading `HH:MM:SS.mmm <seq> <TX|RX> ` prefix of a raw
// SAS-capture line — the same shape `sasLineColor` above reads token[2]
// from. `<seq>` is a monotonic counter with no fixed width (1 digit early
// in a session, more as it climbs), so this is computed PER LINE from the
// actual text rather than a single hardcoded width.
const SAS_LINE_PREFIX_RE = /^\d{2}:\d{2}:\d{2}\.\d{3}\s+\d+\s+(?:TX|RX)\s+/;

/**
 * Character count of `line`'s leading `HH:MM:SS.mmm <seq> <TX|RX> ` prefix
 * — i.e. exactly how far in the FIRST HEX BYTE starts. Returns 0 for a line
 * that doesn't match the shape (non-sas lines, or a general-poll/marker
 * line with no direction token) — no hanging indent for those.
 *
 * Used as a monospace `ch`-unit hanging indent so a wrapped continuation of
 * a long capture line aligns under the first hex byte rather than under the
 * timestamp — display-only (the underlying `line` text, and everything
 * Copy/Download/the analyser handoff read off it, is untouched).
 */
export function computeSasLinePrefixWidth(line: string): number {
  const m = SAS_LINE_PREFIX_RE.exec(line);
  return m ? m[0].length : 0;
}

function ConsoleTerminal({ frames, sessionOpenedAt }: { frames: ConsoleFrame[]; sessionOpenedAt: Date | null }) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const atBottomRef = useRef(true);

  // Autoscroll to the bottom on new frames, but only if the user hasn't
  // scrolled up to read history (terminal-tail behaviour).
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const slack = 24; // px tolerance
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= slack;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [frames]);

  return (
    <Box
      ref={scrollRef}
      onScroll={onScroll}
      data-testid="console-scrollback"
      sx={{
        flex: 1,
        minHeight: 320,
        maxHeight: '55vh',
        overflowY: 'auto',
        bgcolor: '#0b1020',
        color: '#d7e0f5',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: 12.5,
        lineHeight: 1.5,
        px: 1.5,
        py: 1,
      }}
    >
      {sessionOpenedAt && (
        <Typography
          data-testid="session-opened-header"
          variant="caption"
          sx={{ display: 'block', mb: 0.5, color: 'rgba(215,224,245,0.6)', fontFamily: 'inherit' }}
        >
          {formatSessionOpened(sessionOpenedAt)}
        </Typography>
      )}
      {frames.length === 0 ? (
        <Typography
          variant="caption"
          sx={{ color: 'rgba(215,224,245,0.5)', fontFamily: 'inherit' }}
        >
          Waiting for the HAT to stream diagnostics…
        </Typography>
      ) : (
        frames.map((f) => {
          // Hanging indent (display-only — `f.line` itself, and every
          // Copy/Download/analyser-handoff read of it, is untouched): a
          // wrapped continuation of a long capture line lands under the
          // first hex byte, not under the timestamp. `padding-left` pushes
          // every wrapped line right by the prefix width; the matching
          // negative `text-indent` pulls only the FIRST line back to the
          // left edge, so it reads exactly as before.
          const prefixChars = f.kind === 'sas' ? computeSasLinePrefixWidth(f.line) : 0;
          return (
            <Box
              key={f.seq}
              data-testid="console-line"
              sx={{ display: 'flex', gap: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {f.kind && f.kind !== 'diag' && (
                <Box component="span" sx={{ color: '#8fb4ff', flexShrink: 0 }}>[{f.kind}]</Box>
              )}
              <Box
                component="span"
                sx={{
                  display: 'block',
                  color: sasLineColor(f),
                  ...(prefixChars > 0
                    ? { textIndent: `-${prefixChars}ch`, paddingLeft: `${prefixChars}ch` }
                    : null),
                }}
              >
                {f.line}
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dialog.
// ---------------------------------------------------------------------------

/**
 * Remote HAT Console (P0, Mode A). Opened from the IoT-devices list "Console"
 * row action. On open it starts a live diagnostics session on the HAT and tails
 * its hat_diag stream in a terminal-style pane, polling once a second. Closing
 * the dialog (X / Close / Esc) or unmounting ends the session so nothing is left
 * streaming.
 *
 * The HAT is only ever identified by its QR label (never a raw GUID).
 */
export function MachineDeviceConsoleDialog({
  open,
  machineDeviceId,
  qrLabel,
  onClose,
}: {
  open: boolean;
  machineDeviceId: string | null;
  qrLabel: string | null;
  onClose: () => void;
}) {
  const session = useConsoleSession(machineDeviceId, open);
  const [copied, setCopied] = useState(false);

  // Shared scrollback→text assembly — Copy (clipboard) and Download capture
  // (file) must produce identical formatting. Mode-B SAS lines (kind==='sas')
  // already carry their own per-frame HH:MM:SS.mmm capture time, so we don't
  // prefix a second (SC-receive, local-tz) timestamp — that would double-stamp
  // the exported/regulatory artefact. Mode-A diag lines have no embedded time,
  // so they keep the fmtDateTime prefix.
  const scrollbackText = useCallback(
    () =>
      session.frames
        .map((f) => (f.kind === 'sas' ? f.line : `${fmtDateTime(f.t)}  ${f.line}`))
        .join('\n'),
    [session.frames],
  );

  const copyAll = useCallback(async () => {
    const text = scrollbackText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — silent, matches DialogProvider */ }
  }, [scrollbackText]);

  // Saves the current scrollback to disk as a .txt file. Client-side only —
  // there's no server copy of a live console session, so (unlike the CSV/PDF
  // exports elsewhere in the portal) this assembles + downloads locally.
  // Never touches `session.frames`/`phase` — downloading never blanks the
  // live console.
  const downloadCapture = useCallback(() => {
    const blob = new Blob([scrollbackText()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const modeMarker = session.mode === 'B' ? 'sas' : 'diag';
    a.href = url;
    a.download = buildExportFilename('hat-console', `${qrLabel ?? 'hat'}-${modeMarker}`, 'txt');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [scrollbackText, session.mode, qrLabel]);

  // SAS-only snapshot for the analyser handoff — deliberately narrower than
  // scrollbackText() above. If the tech switched A→B mid-session, residual
  // mode-A hat_diag lines are still sitting in `session.frames`; bundling
  // them in would hand the analyser non-SAS text to decode as SAS. Copy /
  // Download keep the full mixed record as-is (unchanged); only this
  // handoff is SAS-only.
  const sasCaptureText = useCallback(
    () => session.frames.filter((f) => f.kind === 'sas').map((f) => f.line).join('\n'),
    [session.frames],
  );
  const hasSasFrames = useMemo(() => session.frames.some((f) => f.kind === 'sas'), [session.frames]);

  // Snapshot the capture currently on screen and hand it to the Machine
  // Comm Analyser in a NEW TAB — the console session/stream here is never
  // touched (no stop, no pause, `session.frames` untouched). Every click
  // mints a fresh one-shot key (`stashConsoleCapture`), so repeat clicks
  // open independent analyser tabs rather than colliding on one stash.
  //
  // Signed-in handoff (belt-and-braces): a `noopener` new tab gets a BRAND
  // NEW, unauthenticated sessionStorage (auth is per-tab), so without this
  // the analyser tab would hit the login screen and lose the capture
  // context. Alongside the capture stash, also stash this tab's OWN access
  // token under a short-lived (60 s), one-shot auth-handoff key — RequireAuth
  // consumes it before its normal "no token → /login" redirect (see
  // auth/RequireAuth.tsx). `getAccessTokenSync` (not the async `getToken`
  // used elsewhere in this file) is deliberate: `window.open` below must run
  // in the SAME synchronous tick as this click for the browser to treat it
  // as a genuine user-gesture popup — an `await` here would risk it being
  // silently blocked. Missing/expired token: proceed with the capture-only
  // handoff (unchanged pre-existing behaviour) rather than blocking the
  // click on an auth concern the analyser tab's own login gate already covers.
  const analyseCapture = useCallback(() => {
    const key = stashConsoleCapture(sasCaptureText());
    const token = getAccessTokenSync();
    const authKey = token ? stashConsoleAuthToken(token) : undefined;
    window.open(buildAnalyserHandoffUrl(key, authKey), '_blank', 'noopener,noreferrer');
  }, [sasCaptureText]);

  const streaming = session.phase === 'streaming';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      <Box sx={{ bgcolor: BRAND_BLUE, color: '#fff', px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" sx={{ flex: 1, fontSize: 17, fontWeight: 500 }}>
          HAT console · {fmtOrEmDash(qrLabel)}
        </Typography>
        {/* Two-slider capture control (single control surface, not a
            protocol change — the setMode/setShowGeneralPolls plumbing below
            is unchanged). "SAS capture" is always visible whenever a session
            exists so its on/off state is never hidden behind a menu; a
            single slide always flips it. "Show general polls" appears
            directly BENEATH it — stacked in its own column, not beside it —
            the instant capture goes on, and disappears the instant capture
            goes off, so it's obvious the filter only means something while
            capture is running. */}
        {(session.phase === 'streaming' || session.phase === 'ended') && (
          <Stack direction="column" spacing={0} sx={{ mr: 0.5 }} data-testid="capture-controls-stack">
            <Tooltip
              title={session.modeBSupported ? '' : "HAT firmware doesn't support SAS capture — update the HAT"}
            >
              <span>
                <FormControlLabel
                  sx={{ ml: 0, mr: 0, color: '#fff' }}
                  control={
                    <Switch
                      size="small"
                      checked={session.mode === 'B'}
                      disabled={!session.modeBSupported}
                      onChange={(_, checked) => session.setMode(checked ? 'B' : 'A')}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: '#fff' }}>SAS capture</Typography>}
                />
              </span>
            </Tooltip>
            {session.mode === 'B' && (
              <FormControlLabel
                sx={{ ml: 0, mr: 0, color: '#fff' }}
                control={
                  <Switch
                    size="small"
                    checked={session.showGeneralPolls}
                    onChange={(_, checked) => session.setShowGeneralPolls(checked)}
                  />
                }
                label={<Typography variant="body2" sx={{ color: '#fff' }}>Show general polls</Typography>}
              />
            )}
          </Stack>
        )}
        {session.truncated && (
          <Tooltip title="The in-browser scrollback dropped its oldest lines — download the capture to keep everything recorded so far.">
            <Chip
              size="small"
              color="warning"
              label="oldest lines dropped — download to keep them"
              data-testid="scrollback-truncated-chip"
            />
          </Tooltip>
        )}
        <SessionStatusChip phase={session.phase} />
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close console"
          sx={{ color: '#fff', opacity: 0.85, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.12)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {session.phase === 'error' && session.error && (
          <ConsoleErrorAlert error={session.error} />
        )}

        {session.phase === 'held' && session.heldBy && (
          <HeldSessionPanel
            heldBy={session.heldBy}
            takingOver={session.takingOver}
            takeoverError={session.takeoverError}
            onTakeOver={session.takeover}
          />
        )}

        {session.phase === 'connecting' && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Connecting to the HAT… (this can take up to 20 s while the site
              controller wakes the diagnostics stream)
            </Typography>
          </Stack>
        )}

        {session.phase !== 'error' && session.phase !== 'connecting' && session.phase !== 'held' && (
          <>
            {session.phase === 'ended' && (
              <Alert severity="info" variant="outlined" sx={{ m: 2, mb: 0 }}>
                <AlertTitle>Console closed</AlertTitle>
                {endReasonLabel(session.endReason)}
              </Alert>
            )}
            {session.modeError && (
              <Alert
                severity="error"
                variant="outlined"
                sx={{ mx: 2, mt: 2 }}
                data-testid="mode-error"
              >
                {session.modeError.message}
              </Alert>
            )}
            {session.captureFilterError && (
              <Alert
                severity="error"
                variant="outlined"
                sx={{ mx: 2, mt: 2 }}
                data-testid="capture-filter-error"
              >
                {session.captureFilterError.message}
              </Alert>
            )}
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: 'text.secondary' }}
              >
                {session.mode === 'B' ? 'SAS capture (raw)' : 'Diagnostics'}
              </Typography>
              <ConsoleTerminal frames={session.frames} sessionOpenedAt={session.sessionOpenedAt} />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Tooltip title={session.paused ? 'Resume live tail' : 'Pause live tail'}>
          <span>
            <Button
              size="small"
              startIcon={session.paused ? <PlayArrowIcon /> : <PauseIcon />}
              disabled={!streaming}
              onClick={() => session.setPaused(!session.paused)}
            >
              {session.paused ? 'Resume' : 'Pause'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={copied ? 'Copied' : 'Copy all lines'}>
          <span>
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              disabled={session.frames.length === 0}
              onClick={copyAll}
            >
              Copy
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Download the full scrollback as a .txt file">
          <span>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              disabled={session.frames.length === 0}
              onClick={downloadCapture}
            >
              Download capture
            </Button>
          </span>
        </Tooltip>
        {session.mode === 'B' && (
          <Tooltip title="Snapshot this capture and open it in the Machine Comm Analyser in a new tab — this console keeps streaming.">
            <span>
              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                disabled={!hasSasFrames}
                onClick={analyseCapture}
                data-testid="console-analyse-capture"
              >
                Analyse capture ↗
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          size="small"
          startIcon={<DeleteSweepIcon />}
          disabled={session.frames.length === 0}
          onClick={session.clear}
        >
          Clear
        </Button>
        <Box sx={{ flex: 1 }} />
        {streaming && (
          <Button size="small" color="error" onClick={session.stop} data-testid="console-stop">
            Stop
          </Button>
        )}
        <Button variant="contained" onClick={onClose} sx={{ bgcolor: BRAND_BLUE, '&:hover': { bgcolor: '#152748' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SessionStatusChip({ phase }: { phase: ConsoleSessionState['phase'] }) {
  const map: Record<ConsoleSessionState['phase'], { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
    connecting: { label: 'connecting…', color: 'warning' },
    streaming:  { label: 'live',        color: 'success' },
    held:       { label: 'held by another user', color: 'warning' },
    ended:      { label: 'closed',      color: 'default' },
    error:      { label: 'error',       color: 'error' },
  };
  const { label, color } = map[phase];
  return <Chip size="small" label={label} color={color} sx={{ color: color === 'default' ? undefined : '#fff' }} />;
}

/**
 * Session-resume/takeover UI (console-session-contract.md): rendered when
 * `useConsoleSession` gets a 409 `console_session_active` from ANOTHER user
 * (the caller's OWN in-progress session is resumed silently — see the
 * `resumed: true` handling in the hook — and never reaches this panel).
 * Shows who holds it and since when, and offers a one-click Take-over with
 * its own visible loading/error states — errors here are never swallowed;
 * the panel stays up on failure so the tech can see what happened and retry.
 */
function HeldSessionPanel({
  heldBy,
  takingOver,
  takeoverError,
  onTakeOver,
}: {
  heldBy: HeldBySession;
  takingOver: boolean;
  takeoverError: ApiError | null;
  onTakeOver: () => void;
}) {
  return (
    <Alert severity="warning" variant="outlined" sx={{ m: 2 }} data-testid="console-held-panel">
      <AlertTitle>Console already open</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Console held by {heldBy.heldByDisplayName} since {fmtDateTime(heldBy.startedAt)}.
      </Typography>
      {takeoverError && (
        <Typography variant="body2" color="error" sx={{ mb: 1.5 }} data-testid="console-takeover-error">
          {takeoverError.message}
        </Typography>
      )}
      <Button
        variant="contained"
        size="small"
        onClick={onTakeOver}
        disabled={takingOver}
        startIcon={takingOver ? <CircularProgress size={14} color="inherit" /> : undefined}
        data-testid="console-takeover"
      >
        {takingOver ? 'Taking over…' : 'Take over session'}
      </Button>
    </Alert>
  );
}
